import React, { Component } from "react";
import { Lookup } from "@eres/ejari-api/Entity";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { ContractAnnualCosts, ContractComputeRequest, ContractDiscountType, ContractValueType } from "@eres/ejari-api/Entity/contract";
import { ServerDate } from "@eres/core";
import CurrencyInputField from "../../../../eres/forms-web/CurrencyInputField";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { defaultErrorManager } from "@eres/forms/errormanager";
import FormRow from "@eres/forms-web/FormRow";
import PickerInput from "@eres/forms-web/PickerInput";
import { IdExtractor, lookupConverter } from "@my-dld/utils/LookupUtil";
import PercentageInputField from "../../../../eres/forms-web/PercentageInputField";
import { NonZeroValidator } from "@eres/forms/validator/NonZeroValidator";
import _ from "lodash";
import { checkNonEmpty, checkNonEmptyGtZeo } from "../../utils";
import { ErrorContainer } from "@eres/forms";
import { formatAppDate } from "@my-dld/utils/Utils";

export const ENTIRE_PERIOD: Lookup = {
	Id: ContractValueType.EntirePeriod,
	...LVStrings.entire_period,
};
export const PER_YEAR = {
	Id: ContractValueType.PerYear,
	...LVStrings.year,
};
export const ContractValueTypes = [ ENTIRE_PERIOD, PER_YEAR ];

interface Props {
	type: ContractValueType;
	values: ContractAnnualCosts[];
	onUpdated: () => void;
}

interface State {
}

class ContractValue extends Component<Props, State> {
	values: ( ValueItem | null )[] = [];
	isValid = () => {
		let isValid = true;
		isValid = isValid && this.values.length > 0;
		for ( const value of this.values ) {
			if ( value )
				isValid = isValid && value.isValid();
		}
		return isValid;
	};
	setItemRef = ( a: ValueItem ) => {
		if ( a )
			this.values[ a.props.index ] = a;
		else {
			this.values = this.values.slice( 0, this.props.values?.length );
		}
	};

	render() {
		const values = this.props.values || [];
		return (
			<>
				{
					values.map( ( value, index ) => {
						return <ValueItem
							index={ index }
							onUpdated={ this.props.onUpdated }
							value={ value } key={ "ix-" + index }
							ref={ this.setItemRef }
						/>;
					} )
				}
			</>
		);
	}

	updateComputeRequest( req: ContractComputeRequest ) {
		if ( this.props.type === ContractValueType.EntirePeriod ) {
			const v = this.values && this.values.length > 0 && this.values[ 0 ];
			if ( v ) {
				const d = v.state;
				req.ContractValue = d.Value;
				req.DiscountTypeId = d.DiscountTypeId;
				if ( req.DiscountTypeId === ContractDiscountType.None ) {
					req.DiscountValue = 0;
				} else
					req.DiscountValue = d.DiscountValue;
			}
		} else {
			const v = this.values && this.values.length > 0 && this.values;
			if ( v ) {
				const cv: ContractAnnualCosts[] = [];
				req.AssociatedContractAnnualCosts = cv;
				for ( let i = 0; i < this.props.values.length; i++ ) {
					const iv = this.values[ i ];
					if ( iv )
						iv.addComputeRequest( cv );
				}
			}
		}
	}
}

export default ContractValue;

interface VProps {
	value: ContractAnnualCosts;
	onUpdated: () => void;
	index: number;
}

interface VState extends ContractAnnualCosts {
	errorContainer: ErrorContainer;
}

class ValueItem extends Component<VProps, VState> {
	constructor( props: VProps ) {
		super( props );
		this.state = {
			DiscountTypeId: props.value.DiscountTypeId,
			errorContainer: {},
			Value: props.value.Value,
			DiscountValue: props.value.DiscountValue,
		};
	}

	isValid = () => {
		let isValid = true;
		const state = this.state;
		const errorContainer: ErrorContainer = {};
		isValid = isValid && checkNonEmptyGtZeo( state, "Value", errorContainer );
		isValid = isValid && checkNonEmpty( state, "DiscountTypeId", errorContainer );
		if ( state.DiscountTypeId !== ContractDiscountType.None )
			isValid = isValid && checkNonEmptyGtZeo( state, "DiscountValue", errorContainer );
		this.setState( {
			errorContainer: errorContainer,
		} );
		return isValid;
	};

	componentDidUpdate( prevProps: Readonly<VProps>, prevState: Readonly<VState>, snapshot?: any ) {
		if ( prevProps.value.DiscountTypeId !== this.props.value.DiscountTypeId ) {
			this.setState( { DiscountTypeId: this.props.value.DiscountTypeId } );
		}
		if ( prevState.DiscountTypeId !== this.state.DiscountTypeId ) {
			this.setState( {
				DiscountValue: undefined,
			} );
		}
		if ( !_.isEqual( prevState, this.state ) ) {
			this.props.onUpdated();
		}
	}

	render() {
		const { value } = this.props;
		const { DiscountTypeId } = this.state;
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
			errorManager: defaultErrorManager,
		};
		return <>
			<div className={ "row" }>
				<ValueDateItem label={ Strings.from_date } value={ value.StartDate! }/>
				<ValueDateItem label={ Strings.to_date } value={ value.EndDate! }/>
			</div>
			<FormRow { ...filedProps }>
				<CurrencyInputField
					required={ true }
					id={ "Value" }
					valueValidators={ NonZeroValidator }
					label={ Strings.value }/>
				<PickerInput
					required={ true }
					id={ "DiscountTypeId" }
					toIdentifier={ IdExtractor }
					valueConverter={ lookupConverter }
					label={ Strings.discount_type }
					items={ EjariDiscountTypes }
				/>
				{ DiscountTypeId === ContractDiscountType.Fixed &&
					<CurrencyInputField
						required={ true }
						id={ "DiscountValue" }
						valueValidators={ NonZeroValidator }
						label={ Strings.value }/>
				}
				{ DiscountTypeId === ContractDiscountType.Percent &&
					<PercentageInputField
						required={ true }
						id={ "DiscountValue" }
						valueValidators={ NonZeroValidator }
						label={ Strings.value }/>
				}
			</FormRow>
		</>;
	}

	addComputeRequest( cv: ContractAnnualCosts[] ) {
		const input = this.props.value;
		const state = this.state;
		const r: ContractAnnualCosts = {
			StartDate: input.StartDate,
			EndDate: input.EndDate,
			DiscountTypeId: state.DiscountTypeId,
			Value: state.Value,
		};
		if ( this.state.DiscountTypeId === ContractDiscountType.None ) {

		} else {
			r.DiscountValue = state.DiscountValue;
		}
		cv.push( r );
	}
}

function ValueDateItem( props: { label: string, value: ServerDate | Date } ) {
	const { label, value } = props;
	const d = formatAppDate( value.asDate() );
	return <div className={ "col-md-4" }>
		<div className="form-group">
			<label>{ label }</label>
			<div className="form-control readonly">{ d }</div>
		</div>
	</div>;
}


export const DISCOUNT_TYPE_NONE: Lookup = {
	Id: ContractDiscountType.None,
	...LVStrings.without_discount,
};
export const DISCOUNT_TYPE_FIXED = {
	Id: ContractDiscountType.Fixed,
	...LVStrings.fixed_value,
};
export const DISCOUNT_TYPE_PERCENT = {
	Id: ContractDiscountType.Percent,
	...LVStrings.percentage,
};
export const EjariDiscountTypes = [ DISCOUNT_TYPE_NONE, DISCOUNT_TYPE_FIXED, DISCOUNT_TYPE_PERCENT ];
